import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, KeyRoundIcon, LockIcon, CheckCircle2Icon, CopyIcon, AlertTriangleIcon, UserPlusIcon, ArrowRightIcon } from 'lucide-react';
type Step = 'email' | 'notFound' | 'code' | 'password' | 'success';
interface StoredUser {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  createdAt: string;
}
function generateCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}
export function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Ingresa tu correo electrónico.');
      return;
    }
    setLoading(true);
    const users: StoredUser[] = JSON.parse(localStorage.getItem('awc_users') || '[]');
    const user = users.find(u => u.email === email.toLowerCase().trim());
    if (!user) {
      setLoading(false);
      setStep('notFound');
      return;
    }
    const code = generateCode();
    const expiresAt = Date.now() + 15 * 60 * 1000; // 15 minutes
    localStorage.setItem(`awc_reset_${user.email}`, JSON.stringify({
      code,
      expiresAt
    }));
    setGeneratedCode(code);
    setLoading(false);
    setStep('code');
  };
  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!enteredCode.trim()) {
      setError('Ingresa el código de verificación.');
      return;
    }
    const stored = localStorage.getItem(`awc_reset_${email.toLowerCase().trim()}`);
    if (!stored) {
      setError('Código inválido o expirado. Solicita uno nuevo.');
      return;
    }
    const {
      code,
      expiresAt
    } = JSON.parse(stored);
    if (Date.now() > expiresAt) {
      setError('El código ha expirado. Solicita uno nuevo.');
      return;
    }
    if (enteredCode.trim() !== code) {
      setError('El código es incorrecto.');
      return;
    }
    setStep('password');
  };
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError('Completa ambos campos.');
      return;
    }
    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    const users: StoredUser[] = JSON.parse(localStorage.getItem('awc_users') || '[]');
    const normalizedEmail = email.toLowerCase().trim();
    const idx = users.findIndex(u => u.email === normalizedEmail);
    if (idx === -1) {
      setError('Cuenta no encontrada.');
      setLoading(false);
      return;
    }
    users[idx].password = newPassword;
    localStorage.setItem('awc_users', JSON.stringify(users));
    localStorage.removeItem(`awc_reset_${normalizedEmail}`);
    setLoading(false);
    setStep('success');
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  };
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  const stepLabels: Record<Step, string> = {
    email: 'Recuperar Contraseña',
    notFound: 'Cuenta No Encontrada',
    code: 'Verificar Código',
    password: 'Nueva Contraseña',
    success: 'Listo'
  };
  return <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a] px-4 py-12 font-body" data-id="element-1299">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="w-full max-w-[440px]" data-id="element-1300">
        {/* Logo */}
        <div className="mb-10 text-center" data-id="element-1301">
          <a href="/" className="inline-block font-heading text-[15px] font-black tracking-[3px] text-white uppercase" data-id="element-1302">
            AI <span className="text-[#F5C842]" data-id="element-1303">WEB CLOSER</span>
          </a>
          <div className="mt-2 text-[11px] tracking-[2px] text-[#444] uppercase" data-id="element-1304">
            Recupera tu acceso
          </div>
        </div>

        {/* Card */}
        <div className="overflow-hidden rounded-lg border border-[#1e1e1e] bg-[#111]" data-id="element-1305">
          <div className="flex items-center gap-2 border-b border-[#1e1e1e] bg-[#161616] px-6 py-3.5" data-id="element-1306">
            <div className="h-1.5 w-1.5 rounded-full bg-[#F5C842]" data-id="element-1307"></div>
            <div className="font-heading text-[9px] font-bold tracking-[2px] text-[#F5C842] uppercase" data-id="element-1308">
              {stepLabels[step]}
            </div>
            <div className="ml-auto flex items-center gap-1" data-id="element-1309">
              {(['email', 'code', 'password'] as Step[]).map((s, i) => {
              const isActive = s === step || step === 'success' && true || (['email', 'code', 'password'] as Step[]).indexOf(step) > i;
              return <div key={s} className={`h-1 w-5 rounded-full transition-colors ${isActive && step !== 'notFound' ? 'bg-[#F5C842]' : 'bg-[#2a2a2a]'}`} data-id="element-1310" />;
            })}
            </div>
          </div>

          <div className="px-6 py-6" data-id="element-1311">
            <AnimatePresence mode="wait" data-id="element-1312">
              {/* STEP 1: Email */}
              {step === 'email' && <motion.form key="email" onSubmit={handleEmailSubmit} initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1313">
                  <div className="mb-5 flex items-start gap-3 rounded-md border border-[#1e1e1e] bg-[#0d0d0d] p-3" data-id="element-1314">
                    <MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#F5C842]" data-id="element-1315" />
                    <p className="text-[12px] leading-relaxed text-[#888]" data-id="element-1316">
                      Ingresa el correo de tu cuenta. Te mostraremos un código
                      de verificación para restablecer tu contraseña.
                    </p>
                  </div>

                  {error && <div className="mb-4 rounded-r-lg border border-[#3a1a1a] border-l-[3px] border-l-[#ff6b6b] bg-[#1a0a0a] px-4 py-3 text-[12px] text-[#ff6b6b]" data-id="element-1317">
                      {error}
                    </div>}

                  <div className="mb-6" data-id="element-1318">
                    <label className="mb-1.5 block text-[11px] font-bold tracking-[1px] text-[#555] uppercase" data-id="element-1319">
                      Correo Electrónico
                    </label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" autoFocus className="w-full rounded-md border border-[#1e1e1e] bg-[#0a0a0a] px-4 py-3 text-[13px] text-white placeholder-[#333] outline-none transition-colors focus:border-[#F5C842]" data-id="element-1320" />
                  </div>

                  <button type="submit" disabled={loading} className="w-full rounded-md bg-[#F5C842] px-6 py-3.5 font-heading text-[12px] font-black tracking-[1px] text-black uppercase transition-opacity hover:opacity-90 disabled:opacity-50" data-id="element-1321">
                    {loading ? 'Verificando...' : 'Enviar Código'}
                  </button>
                </motion.form>}

              {/* STEP 1.5: Email Not Found */}
              {step === 'notFound' && <motion.div key="notFound" initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1322">
                  <div className="mb-5 flex items-start gap-3 rounded-md border border-[#3a2a1a] border-l-[3px] border-l-[#F5C842] bg-[#1a1408] p-4" data-id="element-1323">
                    <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#F5C842]" data-id="element-1324" />
                    <div data-id="element-1325">
                      <p className="mb-2 text-[13px] font-bold text-white" data-id="element-1326">
                        No encontramos una cuenta con{' '}
                        <span className="text-[#F5C842]" data-id="element-1327">{email}</span>
                      </p>
                      <p className="text-[12px] leading-relaxed text-[#888]" data-id="element-1328">
                        Esto suele pasar cuando:
                      </p>
                      <ul className="mt-2 space-y-1.5 text-[12px] leading-relaxed text-[#888]" data-id="element-1329">
                        <li className="flex gap-2" data-id="element-1330">
                          <span className="text-[#F5C842]" data-id="element-1331">•</span>
                          <span data-id="element-1332">
                            Estás en un navegador o dispositivo diferente al que
                            usaste para registrarte.
                          </span>
                        </li>
                        <li className="flex gap-2" data-id="element-1333">
                          <span className="text-[#F5C842]" data-id="element-1334">•</span>
                          <span data-id="element-1335">
                            Compraste el curso pero aún no creaste tu cuenta de
                            acceso.
                          </span>
                        </li>
                        <li className="flex gap-2" data-id="element-1336">
                          <span className="text-[#F5C842]" data-id="element-1337">•</span>
                          <span data-id="element-1338">El correo tiene un error de tipeo.</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <a href="/member-signup" className="group mb-3 flex w-full items-center justify-center gap-2 rounded-md bg-[#F5C842] px-6 py-3.5 font-heading text-[12px] font-black tracking-[1px] text-black uppercase transition-opacity hover:opacity-90" data-id="element-1339">
                    <UserPlusIcon className="h-4 w-4" data-id="element-1340" />
                    Crear Mi Cuenta
                    <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-1341" />
                  </a>

                  <button type="button" onClick={() => {
                setStep('email');
                setError('');
              }} className="w-full rounded-md border border-[#1e1e1e] bg-[#0a0a0a] px-6 py-3 text-[12px] font-bold tracking-[1px] text-[#888] uppercase transition-colors hover:border-[#F5C842]/40 hover:text-white" data-id="element-1342">
                    Probar con otro correo
                  </button>

                  <p className="mt-4 text-center text-[11px] leading-relaxed text-[#555]" data-id="element-1343">
                    Usa el mismo correo con el que compraste el curso para
                    mantener tu progreso vinculado.
                  </p>
                </motion.div>}

              {/* STEP 2: Code */}
              {step === 'code' && <motion.form key="code" onSubmit={handleCodeSubmit} initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1344">
                  <div className="mb-5 rounded-md border border-[#F5C842]/30 bg-[#F5C842]/5 p-4" data-id="element-1345">
                    <div className="mb-2 flex items-center gap-2" data-id="element-1346">
                      <KeyRoundIcon className="h-4 w-4 text-[#F5C842]" data-id="element-1347" />
                      <span className="text-[10px] font-bold tracking-[1.5px] text-[#F5C842] uppercase" data-id="element-1348">
                        Tu Código de Verificación
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3" data-id="element-1349">
                      <div className="font-heading text-[28px] font-black tracking-[6px] text-white" data-id="element-1350">
                        {generatedCode}
                      </div>
                      <button type="button" onClick={copyCode} className="flex items-center gap-1.5 rounded-md border border-[#F5C842]/30 bg-[#0a0a0a] px-3 py-2 text-[11px] font-bold tracking-[1px] text-[#F5C842] uppercase transition-colors hover:bg-[#F5C842]/10" data-id="element-1351">
                        {copied ? <>
                            <CheckCircle2Icon className="h-3.5 w-3.5" data-id="element-1352" /> Copiado
                          </> : <>
                            <CopyIcon className="h-3.5 w-3.5" data-id="element-1353" /> Copiar
                          </>}
                      </button>
                    </div>
                    <p className="mt-3 text-[11px] leading-relaxed text-[#666]" data-id="element-1354">
                      Válido por 15 minutos. Cópialo y pégalo abajo para
                      continuar.
                    </p>
                  </div>

                  {error && <div className="mb-4 rounded-r-lg border border-[#3a1a1a] border-l-[3px] border-l-[#ff6b6b] bg-[#1a0a0a] px-4 py-3 text-[12px] text-[#ff6b6b]" data-id="element-1355">
                      {error}
                    </div>}

                  <div className="mb-6" data-id="element-1356">
                    <label className="mb-1.5 block text-[11px] font-bold tracking-[1px] text-[#555] uppercase" data-id="element-1357">
                      Código de 6 dígitos
                    </label>
                    <input type="text" inputMode="numeric" maxLength={6} value={enteredCode} onChange={e => setEnteredCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" autoFocus className="w-full rounded-md border border-[#1e1e1e] bg-[#0a0a0a] px-4 py-3 text-center font-heading text-[18px] font-bold tracking-[8px] text-white placeholder-[#333] outline-none transition-colors focus:border-[#F5C842]" data-id="element-1358" />
                  </div>

                  <button type="submit" className="w-full rounded-md bg-[#F5C842] px-6 py-3.5 font-heading text-[12px] font-black tracking-[1px] text-black uppercase transition-opacity hover:opacity-90" data-id="element-1359">
                    Verificar Código
                  </button>

                  <button type="button" onClick={() => {
                setStep('email');
                setEnteredCode('');
                setError('');
              }} className="mt-3 w-full text-center text-[11px] tracking-[1px] text-[#555] uppercase transition-colors hover:text-[#F5C842]" data-id="element-1360">
                    ← Cambiar correo
                  </button>
                </motion.form>}

              {/* STEP 3: New Password */}
              {step === 'password' && <motion.form key="password" onSubmit={handlePasswordSubmit} initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1361">
                  <div className="mb-5 flex items-start gap-3 rounded-md border border-[#1e1e1e] bg-[#0d0d0d] p-3" data-id="element-1362">
                    <LockIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#F5C842]" data-id="element-1363" />
                    <p className="text-[12px] leading-relaxed text-[#888]" data-id="element-1364">
                      Crea una nueva contraseña para{' '}
                      <span className="text-white" data-id="element-1365">{email}</span>
                    </p>
                  </div>

                  {error && <div className="mb-4 rounded-r-lg border border-[#3a1a1a] border-l-[3px] border-l-[#ff6b6b] bg-[#1a0a0a] px-4 py-3 text-[12px] text-[#ff6b6b]" data-id="element-1366">
                      {error}
                    </div>}

                  <div className="mb-4" data-id="element-1367">
                    <label className="mb-1.5 block text-[11px] font-bold tracking-[1px] text-[#555] uppercase" data-id="element-1368">
                      Nueva Contraseña
                    </label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" autoFocus className="w-full rounded-md border border-[#1e1e1e] bg-[#0a0a0a] px-4 py-3 text-[13px] text-white placeholder-[#333] outline-none transition-colors focus:border-[#F5C842]" data-id="element-1369" />
                  </div>

                  <div className="mb-6" data-id="element-1370">
                    <label className="mb-1.5 block text-[11px] font-bold tracking-[1px] text-[#555] uppercase" data-id="element-1371">
                      Confirmar Contraseña
                    </label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repite tu contraseña" className="w-full rounded-md border border-[#1e1e1e] bg-[#0a0a0a] px-4 py-3 text-[13px] text-white placeholder-[#333] outline-none transition-colors focus:border-[#F5C842]" data-id="element-1372" />
                  </div>

                  <button type="submit" disabled={loading} className="w-full rounded-md bg-[#F5C842] px-6 py-3.5 font-heading text-[12px] font-black tracking-[1px] text-black uppercase transition-opacity hover:opacity-90 disabled:opacity-50" data-id="element-1373">
                    {loading ? 'Guardando...' : 'Restablecer Contraseña'}
                  </button>
                </motion.form>}

              {/* STEP 4: Success */}
              {step === 'success' && <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.3
            }} className="py-6 text-center" data-id="element-1374">
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.1,
                type: 'spring'
              }} className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F5C842]/10" data-id="element-1375">
                    <CheckCircle2Icon className="h-8 w-8 text-[#F5C842]" data-id="element-1376" />
                  </motion.div>
                  <h3 className="mb-2 font-heading text-[16px] font-black tracking-[1px] text-white uppercase" data-id="element-1377">
                    Contraseña Actualizada
                  </h3>
                  <p className="text-[12px] text-[#888]" data-id="element-1378">
                    Te estamos redirigiendo al inicio de sesión...
                  </p>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Back to login link */}
        {step !== 'success' && <div className="mt-6 text-center text-[12px] text-[#555]" data-id="element-1379">
            ¿Recordaste tu contraseña?{' '}
            <a href="/login" className="font-bold text-[#F5C842] transition-colors hover:text-white" data-id="element-1380">
              Inicia Sesión
            </a>
          </div>}
      </motion.div>
    </div>;
}